import { signInWithPopup, signOut } from "firebase/auth";
import {
  collection, query, where,
  getDocs, getDoc, setDoc, updateDoc, deleteDoc,
  doc, serverTimestamp,
} from "firebase/firestore";
import { auth, googleProvider, db } from "../firebase";

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export async function loginWithGoogle() {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

export async function getLoginResult() {
  return null; // redirect flow removed — popup used everywhere
}

export async function logoutUser() {
  return await signOut(auth);
}

// ---------------------------------------------------------------------------
// Slug derivation (mirrors watchctl logic)
// ---------------------------------------------------------------------------

function deriveSlug(email) {
  const local = email.split("@")[0];
  const base  = local
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 20);
  const suffix = Math.random().toString(16).slice(2, 6);
  return `${base}-${suffix}`;
}

// ---------------------------------------------------------------------------
// Tenant resolution
// ---------------------------------------------------------------------------

// resolveTenant — pass targetTenantId to switch workspace
export async function resolveTenant(uid, email, targetTenantId = null) {
  console.log("[Auth] resolveTenant for uid:", uid);
  try {
    const userSnap = await getDoc(doc(db, "users", uid));

    if (userSnap.exists()) {
      const userData = userSnap.data();
      const tenants  = userData.tenants ?? [{ tenantId: userData.tenantId, role: userData.role }];

      // Prefer: explicit target → last used (localStorage) → first in list
      const lastUsed  = localStorage.getItem("last_tenant_id");
      const preferred = targetTenantId ?? lastUsed;
      const active    = preferred
        ? tenants.find(t => t.tenantId === preferred) ?? tenants[0]
        : tenants[0];
      const result = await _buildTenantResult(active.tenantId, active.role, tenants);
      console.log("[Auth] Tenant resolved:", result);
      return result;
    }

    // Check invites by email
    if (email) {
      const invitesSnap = await getDocs(
        query(collection(db, "invites"),
          where("email",  "==", email),
          where("status", "==", "pending")
        )
      );
      if (!invitesSnap.empty) {
        return await _acceptInvite(invitesSnap.docs[0], uid, email);
      }
    }

    // New user — auto-create tenant
    console.log("[Auth] New user — creating tenant automatically");
    const slug = deriveSlug(email ?? uid);
    await createTenantInFirestore(uid, email ?? "", slug);
    return await _buildTenantResult(slug, "admin", [{ tenantId: slug, role: "admin" }], uid);

  } catch (error) {
    console.error("[Auth] resolveTenant failed:", error.message);
    return null;
  }
}

async function _buildTenantResult(tenantId, role, tenants = [], uid = null) {
  const tenantSnap = await getDoc(doc(db, "tenants", tenantId));
  if (!tenantSnap.exists()) return null;
  const tenantData   = tenantSnap.data();
  const tenantStatus = tenantData.status ?? "active";

  const configSnap = await getDoc(doc(db, "tenants", tenantId, "config", "main"));
  const configData = configSnap.exists() ? configSnap.data() : {};

  // Find which of the user's tenants they own (created)
  let ownedTenantId = null;
  if (uid) {
    for (const t of tenants) {
      const snap = await getDoc(doc(db, "tenants", t.tenantId));
      if (snap.exists() && snap.data().owner_uid === uid) {
        ownedTenantId = t.tenantId;
        break;
      }
    }
  }

  return {
    tenantId,
    role,
    tenantStatus,
    tenants,
    ownedTenantId,
    apiBase: configData.api_base ?? null,
    apiKey:  configData.api_key  ?? null,
  };
}

async function _acceptInvite(inviteDoc, uid, email) {
  const tenantId = inviteDoc.data().tenantId;
  const role     = inviteDoc.data().role ?? "viewer";

  // Load existing tenants and add new one
  const existingSnap = await getDoc(doc(db, "users", uid));
  const existingTenants = existingSnap.exists() ? (existingSnap.data().tenants ?? []) : [];
  const updatedTenants = [...existingTenants.filter(t => t.tenantId !== tenantId), { tenantId, role }];

  await setDoc(doc(db, "users", uid), {
    tenantId, role, email,
    tenants: updatedTenants,
    created_at: serverTimestamp(),
  }, { merge: true });

  await setDoc(doc(db, "tenants", tenantId, "users", uid), {
    uid, email, role,
    created_at: serverTimestamp(),
    invited_by: inviteDoc.data().invited_by ?? null,
  });

  await updateDoc(inviteDoc.ref, {
    status: "accepted", accepted_by: uid, accepted_at: serverTimestamp(),
  });

  console.log("[Auth] Invite accepted for tenant:", tenantId);
  return await _buildTenantResult(tenantId, role, [{ tenantId, role }], uid);
}

// ---------------------------------------------------------------------------
// Tenant creation
// ---------------------------------------------------------------------------

export async function createTenantInFirestore(uid, email, slug) {
  await setDoc(doc(db, "tenants", slug), {
    slug,
    status:     "active",
    owner_uid:  uid,
    created_at: serverTimestamp(),
  });

  await setDoc(doc(db, "users", uid), {
    tenantId: slug,
    tenants: [{ tenantId: slug, role: "admin" }],
    role: "admin", email, created_at: serverTimestamp(),
  });

  await setDoc(doc(db, "tenants", slug, "users", uid), {
    uid, email, role: "admin",
    created_at: serverTimestamp(),
    invited_by: null,
  });

  await setDoc(doc(db, "tenants", slug, "config", "main"), {
    api_base: null, api_key: null,
    prompt_system: null, prompt_chat: null,
    search_threshold: 0.3, llm_model: null,
    updated_at: serverTimestamp(), updated_by: uid,
  });

  console.log("[Auth] Tenant created:", slug);
  return slug;
}

// ---------------------------------------------------------------------------
// Create additional workspace for existing user (multi-workspace)
// ---------------------------------------------------------------------------

export async function createAdditionalWorkspace(uid, email) {
  const slug = deriveSlug(email ?? uid);

  await setDoc(doc(db, "tenants", slug), {
    slug, status: "active", owner_uid: uid, created_at: serverTimestamp(),
  });

  await setDoc(doc(db, "tenants", slug, "config", "main"), {
    api_base: null, api_key: null,
    updated_at: serverTimestamp(), updated_by: uid,
  });

  await setDoc(doc(db, "tenants", slug, "users", uid), {
    uid, email, role: "admin", created_at: serverTimestamp(), invited_by: null,
  });

  // Append to existing tenants array — never overwrite
  const userSnap = await getDoc(doc(db, "users", uid));
  const existing = userSnap.exists() ? (userSnap.data().tenants ?? []) : [];
  await updateDoc(doc(db, "users", uid), {
    tenants: [...existing.filter(t => t.tenantId !== slug), { tenantId: slug, role: "admin" }],
  });

  console.log("[Auth] Additional workspace created:", slug);
  return slug;
}

export async function updateTenantSlug(uid, oldSlug, newSlug) {
  // Migrate /tenants doc
  const oldData = (await getDoc(doc(db, "tenants", oldSlug))).data();
  await setDoc(doc(db, "tenants", newSlug), { ...oldData, slug: newSlug });

  // Migrate config
  const configData = (await getDoc(doc(db, "tenants", oldSlug, "config", "main"))).data();
  await setDoc(doc(db, "tenants", newSlug, "config", "main"), configData ?? {});

  // Update /users/{uid}
  await updateDoc(doc(db, "users", uid), { tenantId: newSlug });

  // Update /tenants/{newSlug}/users/{uid}
  const userInTenant = (await getDoc(doc(db, "tenants", oldSlug, "users", uid))).data();
  await setDoc(doc(db, "tenants", newSlug, "users", uid), userInTenant ?? {});

  // Clean up old tenant
  await deleteDoc(doc(db, "tenants", oldSlug, "users", uid));
  await deleteDoc(doc(db, "tenants", oldSlug, "config", "main"));
  await deleteDoc(doc(db, "tenants", oldSlug));

  console.log("[Auth] Tenant slug updated:", oldSlug, "->", newSlug);
}

// ---------------------------------------------------------------------------
// Backend config
// ---------------------------------------------------------------------------

export async function saveBackendConfig(tenantId, uid, { apiBase, apiKey }) {
  await setDoc(
    doc(db, "tenants", tenantId, "config", "main"),
    { api_base: apiBase, api_key: apiKey, updated_at: serverTimestamp(), updated_by: uid },
    { merge: true }
  );
}

// ---------------------------------------------------------------------------
// AI config
// ---------------------------------------------------------------------------

export async function saveAiConfig(tenantId, uid, { promptSystem, promptChat, searchThreshold, llmModel }) {
  await setDoc(
    doc(db, "tenants", tenantId, "config", "main"),
    {
      prompt_system: promptSystem, prompt_chat: promptChat,
      search_threshold: searchThreshold, llm_model: llmModel,
      updated_at: serverTimestamp(), updated_by: uid,
    },
    { merge: true }
  );
}

export async function loadAiConfig(tenantId) {
  const snap = await getDoc(doc(db, "tenants", tenantId, "config", "main"));
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    promptSystem:    d.prompt_system    ?? "",
    promptChat:      d.prompt_chat      ?? "",
    searchThreshold: d.search_threshold ?? 0.3,
    llmModel:        d.llm_model        ?? "",
  };
}

// ---------------------------------------------------------------------------
// User management
// ---------------------------------------------------------------------------

export async function loadTenantUsers(tenantId) {
  const snap = await getDocs(collection(db, "tenants", tenantId, "users"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function loadTenantInvites(tenantId) {
  const snap = await getDocs(collection(db, "tenants", tenantId, "invites"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function inviteUser(tenantId, inviterUid, { email, role }) {
  await setDoc(doc(db, "invites", email), {
    email, role, tenantId, status: "pending",
    invited_by: inviterUid, created_at: serverTimestamp(),
  });
  await setDoc(doc(db, "tenants", tenantId, "invites", email), {
    email, role, status: "pending",
    invited_by: inviterUid, created_at: serverTimestamp(),
  });
}

export async function removeUser(tenantId, uid) {
  // Remove from tenant subcollection
  await deleteDoc(doc(db, "tenants", tenantId, "users", uid));

  // Update /users/{uid} — remove this tenant from array, keep others
  const userSnap = await getDoc(doc(db, "users", uid));
  if (!userSnap.exists()) return;

  const userData      = userSnap.data();
  const updatedTenants = (userData.tenants ?? []).filter(t => t.tenantId !== tenantId);

  if (updatedTenants.length === 0) {
    // No tenants left — delete the user doc entirely
    await deleteDoc(doc(db, "users", uid));
  } else {
    // Switch active tenant to first remaining
    const newActive = updatedTenants[0];
    await updateDoc(doc(db, "users", uid), {
      tenants:  updatedTenants,
      tenantId: newActive.tenantId,
      role:     newActive.role,
    });
  }
}

export async function updateUserRole(tenantId, uid, role) {
  await updateDoc(doc(db, "tenants", tenantId, "users", uid), { role });

  // Update role in /users/{uid} — both root field and tenants array
  const userSnap = await getDoc(doc(db, "users", uid));
  if (!userSnap.exists()) return;
  const userData       = userSnap.data();
  const updatedTenants = (userData.tenants ?? []).map(t =>
    t.tenantId === tenantId ? { ...t, role } : t
  );
  await updateDoc(doc(db, "users", uid), {
    role:    userData.tenantId === tenantId ? role : userData.role,
    tenants: updatedTenants,
  });
}

export async function revokeInvite(tenantId, email) {
  await deleteDoc(doc(db, "tenants", tenantId, "invites", email));
  await deleteDoc(doc(db, "invites", email));
}

// ---------------------------------------------------------------------------
// Delete tenant (removes all Firestore data)
// ---------------------------------------------------------------------------

export async function deleteTenant(tenantId, uid) {
  // Delete all users in tenant
  const usersSnap = await getDocs(collection(db, "tenants", tenantId, "users"));
  for (const userDoc of usersSnap.docs) {
    await deleteDoc(doc(db, "users", userDoc.id));
    await deleteDoc(userDoc.ref);
  }

  // Delete all invites in tenant
  const invitesSnap = await getDocs(collection(db, "tenants", tenantId, "invites"));
  for (const inviteDoc of invitesSnap.docs) {
    await deleteDoc(doc(db, "invites", inviteDoc.data().email));
    await deleteDoc(inviteDoc.ref);
  }

  // Delete config
  await deleteDoc(doc(db, "tenants", tenantId, "config", "main"));

  // Delete tenant root
  await deleteDoc(doc(db, "tenants", tenantId));

  console.log("[Auth] Tenant deleted:", tenantId);
}

// ---------------------------------------------------------------------------
// Public accept invite (called from HeaderBar bell)
// ---------------------------------------------------------------------------

export async function _acceptInvitePublic(inv, uid, email) {
  const tenantId = inv.tenantId;
  const role     = inv.role ?? "viewer";

  const existingSnap    = await getDoc(doc(db, "users", uid));
  const existingTenants = existingSnap.exists() ? (existingSnap.data().tenants ?? []) : [];
  const updatedTenants  = [...existingTenants.filter(t => t.tenantId !== tenantId), { tenantId, role }];

  await setDoc(doc(db, "users", uid), {
    tenantId: existingSnap.exists() ? existingSnap.data().tenantId : tenantId,
    tenants:  updatedTenants,
    role:     existingSnap.exists() ? existingSnap.data().role : role,
    email,
  }, { merge: true });

  await setDoc(doc(db, "tenants", tenantId, "users", uid), {
    uid, email, role, created_at: serverTimestamp(),
    invited_by: inv.invited_by ?? null,
  });

  // Delete both invite docs so admin sees it resolved immediately
  await deleteDoc(doc(db, "invites", email));
  await deleteDoc(doc(db, "tenants", tenantId, "invites", email)).catch(() => {});

  console.log("[Auth] Invite accepted via bell:", tenantId);
}

// ---------------------------------------------------------------------------
// Leave workspace (guest-initiated)
// ---------------------------------------------------------------------------

export async function leaveWorkspace(tenantId, uid, email) {
  // Remove from tenant subcollection
  await deleteDoc(doc(db, "tenants", tenantId, "users", uid));

  // Update /users/{uid} — remove this tenant from array
  const userSnap       = await getDoc(doc(db, "users", uid));
  const existingTenants = userSnap.exists() ? (userSnap.data().tenants ?? []) : [];
  const updatedTenants  = existingTenants.filter(t => t.tenantId !== tenantId);

  if (updatedTenants.length === 0) {
    await deleteDoc(doc(db, "users", uid));
  } else {
    const newActive = updatedTenants[0];
    await updateDoc(doc(db, "users", uid), {
      tenants:  updatedTenants,
      tenantId: newActive.tenantId,
      role:     newActive.role,
    });
  }

  // Create notification for tenant admins
  await setDoc(doc(db, "tenants", tenantId, "notifications", `leave_${uid}_${Date.now()}`), {
    type:       "member_left",
    uid,
    email,
    tenantId,
    message:    `${email} left this workspace`,
    read:       false,
    created_at: serverTimestamp(),
  });

  console.log("[Auth] Left workspace:", tenantId);
}

// ---------------------------------------------------------------------------
// Disconnect backend (clears api_base and api_key for a tenant)
// ---------------------------------------------------------------------------

export async function disconnectBackend(tenantId, uid) {
  await setDoc(
    doc(db, "tenants", tenantId, "config", "main"),
    { api_base: null, api_key: null, updated_at: serverTimestamp(), updated_by: uid },
    { merge: true }
  );
  console.log("[Auth] Backend disconnected for tenant:", tenantId);
}

// ---------------------------------------------------------------------------
// Load config for a specific tenant (api_base + api_key)
// ---------------------------------------------------------------------------

export async function loadTenantConfig(tenantId) {
  const snap = await getDoc(doc(db, "tenants", tenantId, "config", "main"));
  if (!snap.exists()) return { apiBase: null, apiKey: null };
  const d = snap.data();
  return { apiBase: d.api_base ?? null, apiKey: d.api_key ?? null };
}